import { datadogLogs, type LogsEvent } from '@datadog/browser-logs';
import { datadogRum, type RumErrorEvent, type RumEvent } from '@datadog/browser-rum';
import { defineStore } from 'pinia';

import { env } from '@/env';
import { useAuthStore } from '@/stores/auth';
import { useFullstoryStore } from '@/stores/fullstory';
import { useTrackingStore } from '@/stores/tracking';
import { isE2E } from '@/utils/e2e';
import { logger } from '@/utils/logger';

const ORIGIN = String.raw`(?:https?:\/\/[^/?#]+)?`;
// Connect-link tokens and password-reset hashes are path segments; RUM records URLs verbatim.
const SECRET_SEGMENT = new RegExp(
  String.raw`^(${ORIGIN}\/(?:(?:brand|influencer)-connect-[\w-]+\/[^/?#]+|set-password|password_resets)\/)[^/?#]+`,
);
// OAuth codes/state/verifiers and reset user ids ride in the query or fragment.
const SECRET_QUERY = new RegExp(
  String.raw`^${ORIGIN}\/(?:callback|set-password|password_resets)\/`,
);

/** Strips credentials auth and connect routes carry in their URLs. */
export function redactUrl(url: string) {
  const redacted = url.replace(SECRET_SEGMENT, '$1redacted');
  return SECRET_QUERY.test(redacted) ? redacted.replace(/[?#].*$/, '') : redacted;
}

function registerTrackingSessionId() {
  const ddRumSessionId =
    datadogLogs?.getInternalContext()?.session_id ??
    datadogRum?.getInternalContext()?.session_id ??
    null;
  if (!ddRumSessionId) {
    logger.warn(`Datadog Session ID Missing`);
  }
  useTrackingStore().setSessionId(ddRumSessionId);
  return ddRumSessionId;
}

/**
 * Route path template as the RUM view name; Datadog's URL default keeps brand slugs, one view per brand.
 */
export function viewNameForRoute(
  route: { path: string; matched?: readonly { path: string }[] } | null | undefined,
) {
  return route?.matched?.at(-1)?.path ?? route?.path;
}

export const useDatadogStore = defineStore('datadog', () => {
  const authStore = useAuthStore();
  const fullstoryStore = useFullstoryStore();

  const enabled = env.datadogTraceEnabled && !isE2E() && !env.isLocalDev;
  const clientToken = env.datadogClientToken;
  const applicationId = env.datadogApplicationId;
  const mode = env.mode;
  const version = env.revision;
  let initialized = false;

  // Errors we never want to ship to RUM. Two categories, same outcome:
  //   - Transient network state (user navigated mid-request, lost connection) —
  //     not a real bug.
  //   - Benign browser warnings surfaced via window.onerror. ResizeObserver
  //     loop notices are spec-defined behavior (W3C resize-observer §3.4) that
  //     Chromium reports as Errors; the browser handles them on the next frame.
  const IGNORED_ERROR_PATTERNS = [
    /^(AxiosError: )?Network Error$/,
    /^(AxiosError: )?Request aborted$/,
    /^(Uncaught "?)?ResizeObserver loop completed with undelivered notifications\.?"?$/,
    /^(Uncaught "?)?ResizeObserver loop limit exceeded"?$/,
  ];

  // Cancellation, not failure: our own AbortController (route change, superseded Colada query)
  // or a media element's play()/pause() race. Aborting is the intended outcome, never a bug.
  const ABORTED_ERROR_PATTERNS = [
    /^(AxiosError: )?canceled$/,
    /^signal is aborted without reason$/,
    /^The operation was aborted\.$/,
    /^The user aborted a request\.$/,
    /^The play\(\) request was interrupted/,
  ];

  // Request timeouts: client-perceived slowness, not JS bugs — log a breadcrumb, drop from RUM.
  const TIMEOUT_ERROR_PATTERNS = [
    /^(AxiosError: )?timeout exceeded$/,
    /^(AxiosError: )?timeout of \d+ms exceeded$/,
  ];

  function isIgnoredError(event: RumEvent) {
    if (event.type !== 'error') return false;
    const message = event.error?.message ?? '';
    return [...IGNORED_ERROR_PATTERNS, ...ABORTED_ERROR_PATTERNS].some((pattern) =>
      pattern.test(message),
    );
  }

  function isTimeoutError(event: RumEvent) {
    if (event.type !== 'error') return false;
    const message = event.error?.message ?? '';
    return TIMEOUT_ERROR_PATTERNS.some((pattern) => pattern.test(message));
  }

  function beforeSend(event: RumEvent) {
    if (event.view?.url) event.view.url = redactUrl(event.view.url);
    if (event.view?.referrer) event.view.referrer = redactUrl(event.view.referrer);
    if (event.type === 'resource') event.resource.url = redactUrl(event.resource.url);
    if (event.type === 'error' && event.error?.resource) {
      event.error.resource.url = redactUrl(event.error.resource.url);
    }
    if (isIgnoredError(event)) return false;
    if (isTimeoutError(event)) {
      // isTimeoutError only matches error events.
      const { error } = event as RumErrorEvent;
      logger.warn('Request timed out', {
        errorMessage: error.message,
        resourceUrl: error.resource?.url,
      });
      return false;
    }
    registerTrackingSessionId();
    if (event.type === 'error' && fullstoryStore.isFullStoryEnabled) {
      event.context = {
        ...event.context,
        fullStory: {
          fullStoryUrl: fullstoryStore.getFullStoryUrl(),
        },
      };

      try {
        fullstoryStore.sendFullstoryEvent('RUM Error', { ...event });
      } catch {
        logger.debug('Unable to report RUM error details to FullStory');
      }
    }
    return true;
  }

  // Logs-SDK counterpart to beforeSend — drop the same transient noise console forwarding leaks in.
  function logsBeforeSend(log: LogsEvent | null | undefined) {
    if (log?.view?.url) log.view.url = redactUrl(log.view.url);
    if (log?.view?.referrer) log.view.referrer = redactUrl(log.view.referrer);
    if (log?.http?.url) log.http.url = redactUrl(log.http.url);
    const message = log?.message ?? '';
    return ![...IGNORED_ERROR_PATTERNS, ...ABORTED_ERROR_PATTERNS, ...TIMEOUT_ERROR_PATTERNS].some(
      (pattern) => pattern.test(message),
    );
  }

  function init() {
    if (enabled && !initialized) {
      const commonConfig = {
        clientToken,
        service: 'dash-web',
        env: mode,
        version,
        site: 'datadoghq.com',
        proxy: (options: { path: string; parameters: string }) =>
          `https://dd.dashsocial.com/${options.path}?${options.parameters}`,
      };
      // running vue 3 migration with datadog logs causes issues
      if (env.isProduction) {
        datadogLogs.init({
          ...commonConfig,
          sessionSampleRate: 100,
          forwardErrorsToLogs: false,
          forwardConsoleLogs: ['warn', 'error'],
          forwardReports: ['csp_violation', 'intervention'],
          beforeSend: logsBeforeSend,
        });
      }
      datadogRum.init({
        ...commonConfig,
        applicationId,
        allowedTracingUrls: [
          (url) => {
            // ChiliPiper calls include our full domain in the URL, causing x-datadog-* headers
            // being added to requests to ChiliPiper, who disallow those headers
            if (url.includes('chilipiper') || url.includes('hubspot')) {
              return false;
            }
            return [
              /https:\/\/(?!cdn|images|mp).*\.dhdev\.co/,
              /https:\/\/(?!cdn|images|mp).*\.dashhudson\.com/,
              /https:\/\/(?!cdn|images|mp).*\.dashsocial\.com/,
            ].some((expr) => expr.test(url));
          },
        ],
        sessionSampleRate: 100,
        sessionReplaySampleRate: 0,
        traceSampleRate: 100,
        trackResources: true,
        beforeSend,
      });
      initialized = true;
      registerTrackingSessionId();
    }
  }

  function setupUser() {
    if (enabled) {
      const identity = authStore.identity;
      const currentBrand = authStore.currentBrand;

      if (identity) {
        const user = {
          // Datadog types usr.id as a string.
          id: String(identity.id),
          name: `${identity.first_name} ${identity.last_name}`,
          email: identity.email,
        };
        datadogLogs.setUser(user);
        datadogRum.setUser(user);
      } else {
        datadogLogs.clearUser();
        datadogRum.clearUser();
      }

      if (currentBrand) {
        const brand = {
          id: currentBrand.id,
          name: currentBrand.name,
          label: currentBrand.label,
        };
        datadogLogs.setGlobalContextProperty('brand', brand);
        datadogRum.setGlobalContextProperty('brand', brand);
      } else {
        datadogLogs.removeGlobalContextProperty('brand');
        datadogRum.removeGlobalContextProperty('brand');
      }
    }
  }

  function addError(...args: Parameters<typeof datadogRum.addError>) {
    if (enabled) {
      datadogRum.addError(...args);
    }
  }

  function addAction(...args: Parameters<typeof datadogRum.addAction>) {
    if (enabled) {
      datadogRum.addAction(...args);
    }
  }

  function addDurationVital(...args: Parameters<typeof datadogRum.addDurationVital>) {
    if (enabled) {
      datadogRum.addDurationVital(...args);
    }
  }

  function setViewName(name: string) {
    if (enabled) {
      datadogRum.setViewName(name);
    }
  }

  return {
    init,
    setupUser,
    beforeSend,
    logsBeforeSend,
    addError,
    addAction,
    addDurationVital,
    setViewName,
  };
});
